import React from "react";
import Chart from "react-apexcharts";
import {
  IconBox,
  IconCurrencyRupeeNepalese,
  IconAlertTriangle,
  IconTool,
  IconCheck,
  IconClock,
  IconCalendarTime,
  IconShieldCheck,
  IconTrash,
  IconFileReport,
  IconScan,
  IconCalculator,
  IconPlus,
} from "@tabler/icons-react";
import { iconStroke } from "../../config/config";
import { useStoreSettings } from "../../controllers/settings.controller";
import { useAssetDashboard } from "../../controllers/assets.controller";

export default function AssetDashboardTab({ dashboard: propDashboard, onNavigateTab, onNavigate, onOpenAddAsset }) {
  const { data: fetchedDashboard } = useAssetDashboard();
  const { data: storeSettings } = useStoreSettings();
  const currency = storeSettings?.currency || "Rs.";

  const stats = propDashboard || fetchedDashboard || {};
  const navigateFn = onNavigateTab || onNavigate || (() => {});

  // Charts configuration
  const categoryLabels = (stats.category_distribution || []).map((c) => c.name);
  const categoryValues = (stats.category_distribution || []).map((c) => parseFloat(c.total_value || 0));

  const categoryChartOptions = {
    chart: { type: "donut", toolbar: { show: false } },
    labels: categoryLabels.length > 0 ? categoryLabels : ["No Assets"],
    colors: ["#10b981", "#3b82f6", "#f59e0b", "#ec4899", "#8b5cf6", "#06b6d4", "#64748b"],
    legend: { position: "bottom" },
    dataLabels: { enabled: false },
    tooltip: {
      y: {
        formatter: (val) => `${currency} ${Number(val).toLocaleString()}`,
      },
    },
  };
  const categoryChartSeries = categoryValues.length > 0 ? categoryValues : [1];

  const statusLabels = (stats.status_distribution || []).map((s) => s.status);
  const statusCounts = (stats.status_distribution || []).map((s) => parseInt(s.count || 0, 10));

  const statusChartOptions = {
    chart: { type: "donut", toolbar: { show: false } },
    labels: statusLabels.length > 0 ? statusLabels : ["No Data"],
    colors: ["#10b981", "#0284c7", "#f59e0b", "#ef4444", "#8b5cf6", "#64748b", "#cbd5e1"],
    legend: { position: "bottom" },
    dataLabels: { enabled: true },
  };
  const statusChartSeries = statusCounts.length > 0 ? statusCounts : [1];

  const maintTrendLabels = (stats.maintenance_expenditure_trend || []).map((m) => m.month_label);
  const maintTrendValues = (stats.maintenance_expenditure_trend || []).map((m) => parseFloat(m.total_cost || 0));

  const maintChartOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    colors: ["#ef4444"],
    xaxis: { categories: maintTrendLabels.length > 0 ? maintTrendLabels : ["No Recent Months"] },
    yaxis: {
      labels: {
        formatter: (val) => `${currency} ${Number(val).toLocaleString()}`,
      },
    },
    dataLabels: { enabled: false },
  };
  const maintChartSeries = [
    {
      name: "Maintenance Cost",
      data: maintTrendValues.length > 0 ? maintTrendValues : [0],
    },
  ];

  return (
    <div className="space-y-6">
      {/* Top Banner & Quick Actions */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-700 text-white rounded-2xl p-6 shadow-md flex flex-col md:flex-row items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">Hotel Asset Lifecycle & Financial Hub</h2>
          <p className="text-emerald-100 text-sm mt-1">
            Track fixed assets, capitalization, preventive servicing, depreciation runs, and custody transfers.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={onOpenAddAsset}
            className="btn btn-sm bg-white text-emerald-800 hover:bg-emerald-50 border-0 font-semibold flex items-center gap-1 shadow"
          >
            <IconPlus size={18} stroke={iconStroke} /> Register Asset
          </button>
          <button
            onClick={() => navigateFn("damage")}
            className="btn btn-sm bg-emerald-800/80 hover:bg-emerald-800 text-white border-0 flex items-center gap-1"
          >
            <IconAlertTriangle size={18} stroke={iconStroke} /> Report Damage
          </button>
          <button
            onClick={() => navigateFn("verification")}
            className="btn btn-sm bg-emerald-800/80 hover:bg-emerald-800 text-white border-0 flex items-center gap-1"
          >
            <IconScan size={18} stroke={iconStroke} /> QR Audit
          </button>
          <button
            onClick={() => navigateFn("depreciation")}
            className="btn btn-sm bg-emerald-800/80 hover:bg-emerald-800 text-white border-0 flex items-center gap-1"
          >
            <IconCalculator size={18} stroke={iconStroke} /> Depreciation
          </button>
        </div>
      </div>

      {/* Financial Metric Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div
          onClick={() => navigateFn("register")}
          className="bg-white dark:bg-neutral-800 p-4 rounded-xl border border-gray-100 dark:border-neutral-700 shadow-sm cursor-pointer hover:border-emerald-500 transition-colors"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Total Assets</span>
            <div className="p-2 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 rounded-lg">
              <IconBox size={20} stroke={iconStroke} />
            </div>
          </div>
          <div className="text-2xl font-black text-gray-800 dark:text-white mt-2">
            {Number(stats.total_assets || 0).toLocaleString()}
          </div>
          <div className="text-xs text-gray-400 mt-1">Identifiable equipment records</div>
        </div>

        <div
          onClick={() => navigateFn("register")}
          className="bg-white dark:bg-neutral-800 p-4 rounded-xl border border-gray-100 dark:border-neutral-700 shadow-sm cursor-pointer hover:border-emerald-500 transition-colors"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Total Cost</span>
            <div className="p-2 bg-blue-50 dark:bg-blue-950/40 text-blue-600 rounded-lg">
              <IconCurrencyRupeeNepalese size={20} stroke={iconStroke} />
            </div>
          </div>
          <div className="text-2xl font-black text-gray-800 dark:text-white mt-2">
            {currency} {Number(stats.total_cost || 0).toLocaleString()}
          </div>
          <div className="text-xs text-gray-400 mt-1">Capitalized acquisition base</div>
        </div>

        <div
          onClick={() => navigateFn("depreciation")}
          className="bg-white dark:bg-neutral-800 p-4 rounded-xl border border-gray-100 dark:border-neutral-700 shadow-sm cursor-pointer hover:border-emerald-500 transition-colors"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Accumulated Dep.</span>
            <div className="p-2 bg-amber-50 dark:bg-amber-950/40 text-amber-600 rounded-lg">
              <IconClock size={20} stroke={iconStroke} />
            </div>
          </div>
          <div className="text-2xl font-black text-gray-800 dark:text-white mt-2">
            {currency} {Number(stats.accumulated_depreciation || 0).toLocaleString()}
          </div>
          <div className="text-xs text-gray-400 mt-1">Total posted depreciation</div>
        </div>

        <div
          onClick={() => navigateFn("depreciation")}
          className="bg-white dark:bg-neutral-800 p-4 rounded-xl border border-gray-100 dark:border-neutral-700 shadow-sm cursor-pointer hover:border-emerald-500 transition-colors"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Net Book Value</span>
            <div className="p-2 bg-purple-50 dark:bg-purple-950/40 text-purple-600 rounded-lg">
              <IconCalculator size={20} stroke={iconStroke} />
            </div>
          </div>
          <div className="text-2xl font-black text-purple-600 dark:text-purple-400 mt-2">
            {currency} {Number(stats.current_book_value || 0).toLocaleString()}
          </div>
          <div className="text-xs text-gray-400 mt-1">Current balance sheet value</div>
        </div>
      </div>

      {/* Operational Status Pills */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <div
          onClick={() => navigateFn("register")}
          className="p-3 bg-white dark:bg-neutral-800 rounded-xl border border-gray-100 dark:border-neutral-700 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-neutral-700"
        >
          <div>
            <div className="text-xs text-gray-500">In Use / Assigned</div>
            <div className="text-lg font-bold text-gray-800 dark:text-gray-100">{stats.assets_in_use || 0}</div>
          </div>
          <span className="w-2.5 h-2.5 rounded-full bg-blue-500"></span>
        </div>

        <div
          onClick={() => navigateFn("register")}
          className="p-3 bg-white dark:bg-neutral-800 rounded-xl border border-gray-100 dark:border-neutral-700 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-neutral-700"
        >
          <div>
            <div className="text-xs text-gray-500">Available</div>
            <div className="text-lg font-bold text-emerald-600">{stats.assets_available || 0}</div>
          </div>
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span>
        </div>

        <div
          onClick={() => navigateFn("maintenance")}
          className="p-3 bg-white dark:bg-neutral-800 rounded-xl border border-gray-100 dark:border-neutral-700 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-neutral-700"
        >
          <div>
            <div className="text-xs text-gray-500">Under Repair</div>
            <div className="text-lg font-bold text-amber-600">{stats.assets_under_maintenance || 0}</div>
          </div>
          <span className="w-2.5 h-2.5 rounded-full bg-amber-500"></span>
        </div>

        <div
          onClick={() => navigateFn("damage")}
          className="p-3 bg-white dark:bg-neutral-800 rounded-xl border border-gray-100 dark:border-neutral-700 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-neutral-700"
        >
          <div>
            <div className="text-xs text-gray-500">Damaged</div>
            <div className="text-lg font-bold text-red-600">{stats.assets_damaged || 0}</div>
          </div>
          <span className="w-2.5 h-2.5 rounded-full bg-red-500"></span>
        </div>

        <div
          onClick={() => navigateFn("preventive")}
          className="p-3 bg-white dark:bg-neutral-800 rounded-xl border border-gray-100 dark:border-neutral-700 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-neutral-700"
        >
          <div>
            <div className="text-xs text-gray-500">Overdue PM</div>
            <div className="text-lg font-bold text-orange-600">{stats.overdue_preventive || 0}</div>
          </div>
          <span className="w-2.5 h-2.5 rounded-full bg-orange-500"></span>
        </div>

        <div
          onClick={() => navigateFn("register")}
          className="p-3 bg-white dark:bg-neutral-800 rounded-xl border border-gray-100 dark:border-neutral-700 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-neutral-700"
        >
          <div>
            <div className="text-xs text-gray-500">Expiring Warranty</div>
            <div className="text-lg font-bold text-indigo-600">{stats.expiring_warranties || 0}</div>
          </div>
          <span className="w-2.5 h-2.5 rounded-full bg-indigo-500"></span>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-neutral-800 p-5 rounded-2xl border border-gray-100 dark:border-neutral-700 shadow-sm">
          <h3 className="font-bold text-gray-800 dark:text-white text-base mb-2">Book Value by Category</h3>
          <div className="h-64 flex items-center justify-center">
            <Chart options={categoryChartOptions} series={categoryChartSeries} type="donut" width="100%" height={240} />
          </div>
        </div>

        <div className="bg-white dark:bg-neutral-800 p-5 rounded-2xl border border-gray-100 dark:border-neutral-700 shadow-sm">
          <h3 className="font-bold text-gray-800 dark:text-white text-base mb-2">Assets by Lifecycle Status</h3>
          <div className="h-64 flex items-center justify-center">
            <Chart options={statusChartOptions} series={statusChartSeries} type="donut" width="100%" height={240} />
          </div>
        </div>

        <div className="bg-white dark:bg-neutral-800 p-5 rounded-2xl border border-gray-100 dark:border-neutral-700 shadow-sm">
          <h3 className="font-bold text-gray-800 dark:text-white text-base mb-2">Maintenance Spend (Last 6 Months)</h3>
          <div className="h-64 flex items-center justify-center">
            <Chart options={maintChartOptions} series={maintChartSeries} type="bar" width="100%" height={240} />
          </div>
        </div>
      </div>
    </div>
  );
}
